import { useState } from 'react';
import { factionOf, isSpecialRole, type PlayerView } from '@bm/shared';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';
import { AppShell } from '@/components/AppShell';
import { PhaseChip } from '@/components/PhaseChip';
import { Button } from '@/components/Button';
import { HistorySheet } from '@/components/HistorySheet';
import { ConfirmSheet } from '@/components/ConfirmSheet';

const REASON_HINT = {
  ALL_MAFIA_ELIMINATED: de.gameOver.citizensWinHint,
  MAFIA_PARITY: de.gameOver.mafiaWinsHint,
  MAX_TIES: de.gameOver.mafiaWinsMaxTies,
} as const;

export function GameOverScreen() {
  const view = useAppStore((s) => s.view);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  if (!view || !view.outcome) return null;

  const { outcome } = view;
  const mafiaWins = outcome.winner === 'MAFIA';
  // Über die Fraktion statt über einzelne Rollen: sonst fällt jede Sonderrolle
  // aus beiden Listen und wäre bei der Auflösung schlicht nicht da.
  const mafia = view.players.filter((p) => p.role !== null && factionOf(p.role) === 'MAFIA');
  const citizens = view.players.filter((p) => p.role !== null && factionOf(p.role) === 'CITIZENS');

  return (
    <AppShell
      header={<PhaseChip phase={view.phase} round={view.roundNumber} />}
      footer={
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          {view.self.isHost && (
            <Button fullWidth onClick={() => wsClient.send({ type: 'game:reset' })}>
              {de.gameOver.newRound}
            </Button>
          )}
          <Button fullWidth variant="secondary" onClick={() => setHistoryOpen(true)}>
            {de.gameOver.history}
          </Button>
          {/* Der Gegenpol zu „Neue Runde": Ohne ihn bliebe der Raum bis zum
              Ablauf seiner Lebensdauer stehen. */}
          {view.self.isHost && (
            <Button fullWidth variant="ghost" onClick={() => setCloseOpen(true)}>
              {de.common.closeRoom}
            </Button>
          )}
        </div>
      }
    >
      <div className="mx-auto flex max-w-md flex-col items-center gap-8 py-6 text-center">
        <div className="relative animate-winner-in">
          {/* Statischer Schein in der Siegerfarbe — kein Partikeleffekt: „dezent"
              ist eine Vorgabe (GDD §32), und Konfetti kostet auf schwachen
              Handys genau die Frames, die der Screen sonst nicht braucht. */}
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute -inset-x-8 -inset-y-6 rounded-full blur-2xl ${
              mafiaWins ? 'bg-mafia-muted' : 'bg-citizen-muted'
            }`}
          />
          {/* `relative` auch hier: positionierte Geschwister malen über
              statische, der Schein läge sonst über der Schrift. */}
          <div className="relative">
            <p
              className={`font-display text-3xl font-extrabold tracking-tight sm:text-4xl ${
                mafiaWins ? 'text-mafia' : 'text-citizen'
              }`}
            >
              {mafiaWins ? de.gameOver.mafiaWins : de.gameOver.citizensWin}
            </p>
            <p className="mt-2 text-fg-muted">{REASON_HINT[outcome.reason]}</p>
          </div>
        </div>

        {/* Die Auflösung kommt nach dem Ergebnis, nicht mit ihm. `backwards`,
            damit die Karten während der Verzögerung unsichtbar bleiben statt
            aufzublitzen — Muster aus `RoundResultScreen`. */}
        <div className="grid w-full grid-cols-2 gap-4">
          {[
            { label: de.gameOver.mafiaLabel, tone: 'mafia' as const, players: mafia },
            { label: de.gameOver.citizensLabel, tone: 'citizen' as const, players: citizens },
          ].map((faction, index) => (
            <div
              key={faction.tone}
              className="h-full animate-phase-in"
              style={{ animationDelay: `${0.35 + index * 0.12}s`, animationFillMode: 'backwards' }}
            >
              <FactionList {...faction} />
            </div>
          ))}
        </div>
      </div>

      <HistorySheet view={view} open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <ConfirmSheet
        open={closeOpen}
        title={de.common.closeRoomConfirm}
        body={de.common.closeRoomBody}
        confirmLabel={de.common.closeRoomConfirmCta}
        destructive
        onConfirm={() => {
          setCloseOpen(false);
          wsClient.send({ type: 'room:close' });
        }}
        onCancel={() => setCloseOpen(false)}
      />
    </AppShell>
  );
}

function FactionList({
  label,
  tone,
  players,
}: {
  label: string;
  tone: 'mafia' | 'citizen';
  players: PlayerView[];
}) {
  return (
    <div
      className={`h-full rounded-2xl border px-4 py-3 ${
        tone === 'mafia' ? 'border-mafia/40 bg-mafia-muted' : 'border-citizen/40 bg-citizen-muted'
      }`}
    >
      <p className={`mb-2 font-display text-sm font-bold ${tone === 'mafia' ? 'text-mafia' : 'text-citizen'}`}>
        {label}
      </p>
      <ul className="flex flex-col gap-1 text-sm">
        {players.map((player) => (
          <li key={player.id}>
            {player.name}
            {player.role && isSpecialRole(player.role) && (
              <span className="block text-xs text-fg-muted">{de.roles[player.role]}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
